import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBuilding } from '../context/buildingContext';
import { fetchApi } from '../utils/fetchApi';
import { displayFloorAndDoorByName, hashPassword } from '../utils/helpers';
import arrowLeftIcon from '../assets/arrow-left.svg';
import iconCross from '../assets/icon-cross.svg';
import iconCheck from '../assets/icon-check.svg';

// ─ Types ─

interface SubUser {
  userId: number;
  username: string;
  email: string;
  isActive: boolean;
  expiresAt: string | null;
}

// ─ Helpers ─

const MAX_USERS = 10;
const SUB_USER_ROLE = 6;
const MAX_USERNAME_ATTEMPTS = 20;

const generatePassword = (): string => {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  return Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};

const toSlug = (value: string): string =>
  value.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

// Short building code from the street: first word + house number, e.g. "Kossuth utca 23/A" -> "kossuth23a"
const buildingCodeFromStreet = (street: string | null): string => {
  if (!street) return '';
  const words = street.trim().split(/\s+/);
  const first = toSlug(words[0] ?? '');
  const houseNumber = words.length > 1 && /\d/.test(words[words.length - 1]) ? toSlug(words[words.length - 1]) : '';
  return first + houseNumber;
};

// Format: fizikus_<building>_<door>_<MMDD>_<index>, e.g. fizikus_kossuth23a_021_1026_1
const generateUsername = (buildingCode: string, apartmentName: string | null, index: number): string => {
  // Same door number that is shown in the UI; fall back to the full name if it can't be parsed
  const door = displayFloorAndDoorByName(apartmentName, true);
  const apartment = toSlug(String(door || apartmentName || '')) || 'ap';
  const now = new Date();
  const monthDay = `${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
  const parts = ['fizikus', buildingCode, apartment, monthDay, String(index)].filter(Boolean);
  return parts.join('_');
};

const isExpired = (user: SubUser): boolean => {
  if (!user.expiresAt) return false;
  return new Date(user.expiresAt) < new Date();
};

const formatExpiry = (iso: string, locale: string): string => {
  const d = new Date(iso);
  return d.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getNextIndex = (users: SubUser[]): number => {
  if (users.length === 0) return 1;
  const max = users.reduce((acc, u) => {
    const match = u.username.match(/_(\d+)$/);
    return match ? Math.max(acc, parseInt(match[1])) : acc;
  }, 0);
  return max + 1;
};

// ─ Component ─

const SubUsers: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { buildingId, apartmentId, apartmentName } = useBuilding();

  // ── State ──
  const [users, setUsers] = useState<SubUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Stores passwords only for users created in this session
  const [passwordMap, setPasswordMap] = useState<Map<number, string>>(new Map());

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createEmail, setCreateEmail] = useState('');
  const [expiryEnabled, setExpiryEnabled] = useState(false);
  const [expiryDays, setExpiryDays] = useState('');
  const [expiryTime, setExpiryTime] = useState('12:00');

  const [newlyCreated, setNewlyCreated] = useState<(SubUser & { password?: string; isExisting: boolean }) | null>(null);
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);

  // Loading states
  const [isCreating, setIsCreating] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // ── Fetch ──
  const fetchUsers = useCallback(async () => {
    if (!apartmentId) return;
    setIsLoading(true);
    try {
      const result = await fetchApi('get_users_by_location', {
        apartment_id: parseInt(apartmentId),
      });
      const subUsers: SubUser[] = (result as any[])
        .filter((u: any) => u.access_type === 'ap_resident')
        .map((u: any) => ({
          userId: u.user_id,
          username: u.name,
          email: u.email ?? '',
          isActive: u.active ?? true,
          expiresAt: u.valid_until ?? null,
        }));
      setUsers(subUsers);
    } catch (err) {
      console.error('Failed to load sub-users:', err);
    } finally {
      setIsLoading(false);
    }
  }, [apartmentId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // ── Handlers ──

  const handleCreate = async () => {
    setIsCreating(true);
    try {
      const email = createEmail.trim().toLowerCase();
      let backendUserId: number;
      let isExisting = false;
      let newPassword: string | undefined;
      let newUsername = '';
      let expiresAt: string | null = null;

      // Check if user already exists in the system
      let existingId: number | null = null;
      try {
        const check = await fetchApi('check_email_is_valid', { email });
        existingId = check.user_id;
      } catch {
        // User not found — will be created fresh
      }

      if (existingId !== null) {
        // User already exists — only add apartment access
        isExisting = true;
        backendUserId = existingId;
      } else {
        // New user — full registration
        newPassword = generatePassword();

        // Building code for the username (street + house number, or building name as fallback)
        let buildingCode = '';
        try {
          const buildings = await fetchApi('get_building_access_rights', {});
          // Row layout: [id, country, postalCode, city, street, temperature, humidity, name]
          const match = (buildings as any[]).find((b: any[]) => b[0] === parseInt(buildingId ?? ''));
          if (match) buildingCode = buildingCodeFromStreet(match[4]) || toSlug(match[7] ?? '');
        } catch (err) {
          console.error('Failed to load building for username:', err);
        }
        if (!buildingCode && buildingId) buildingCode = `b${buildingId}`;
        const pwhash = await hashPassword(newPassword);

        if (expiryEnabled && expiryDays) {
          const days = parseInt(expiryDays);
          if (!isNaN(days) && days > 0) {
            const expiry = new Date();
            expiry.setDate(expiry.getDate() + days);
            if (expiryTime) {
              const [h, m] = expiryTime.split(':').map(Number);
              expiry.setHours(h, m, 0, 0);
            }
            expiresAt = expiry.toISOString();
          }
        }

        // Usernames of users removed from this apartment still exist in the DB,
        // so step the index forward until a free username is found.
        let index = getNextIndex(users);
        let registerResult: any = null;
        for (let attempt = 0; attempt < MAX_USERNAME_ATTEMPTS; attempt++, index++) {
          newUsername = generateUsername(buildingCode, apartmentName, index);
          try {
            registerResult = await fetchApi('register_user', {
              username: newUsername,
              email,
              pwhash,
              password: newPassword,
              role: SUB_USER_ROLE,
              ...(expiresAt ? { valid_until: expiresAt } : {}),
              active: true,
            });
            break;
          } catch (err) {
            // The email was already checked above, so a duplicate here means the username is taken
            const msg = String(err);
            const usernameTaken = msg.includes('Username already exists') || msg.includes('Username or email already exists');
            if (!usernameTaken || attempt === MAX_USERNAME_ATTEMPTS - 1) throw err;
          }
        }
        backendUserId = registerResult.user_id;
      }

      // Add apartment access right for both new and existing users
      if (apartmentId) {
        await fetchApi('add_apartment_access_right', {
          user_id: backendUserId,
          apartment_id: parseInt(apartmentId),
          rights: 0,
        });

        // For existing users send a separate notification email
        if (isExisting) {
          await fetchApi('send_apartment_access_email', {
            user_id: backendUserId,
            apartment_id: parseInt(apartmentId),
          });
        }
      }

      if (newPassword) {
        setPasswordMap(prev => new Map(prev).set(backendUserId, newPassword!));
      }

      setShowCreateForm(false);
      setCreateEmail('');
      setExpiryEnabled(false);
      setExpiryDays('');
      setExpiryTime('12:00');
      setNewlyCreated({
        userId: backendUserId,
        username: newUsername,
        email,
        isActive: true,
        expiresAt,
        password: newPassword,
        isExisting,
      });

      await fetchUsers();
    } catch (err) {
      console.error('Create sub-user failed:', err);
      alert(String(err));
    } finally {
      setIsCreating(false);
    }
  };

  const handleToggleActive = async (userId: number) => {
    const user = users.find(u => u.userId === userId);
    if (!user) return;

    const newActive = !user.isActive;
    setTogglingId(userId);
    try {
      await fetchApi('set_user_active', {
        user_id: userId,
        active: newActive,
      });
      setUsers(prev =>
        prev.map(u => (u.userId === userId ? { ...u, isActive: newActive } : u))
      );
    } catch (err) {
      console.error('Toggle active failed:', err);
      alert(String(err));
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (userId: number) => {
    setDeletingId(userId);
    try {
      // Only remove apartment access — do not delete the user account
      if (apartmentId) {
        await fetchApi('delete_apartment_access_right', {
          user_id: userId,
          apartment_id: parseInt(apartmentId),
        });
      }
      setPasswordMap(prev => { const m = new Map(prev); m.delete(userId); return m; });
      if (expandedUserId === userId) setExpandedUserId(null);
      await fetchUsers();
    } catch (err) {
      console.error('Delete sub-user failed:', err);
      alert(String(err));
    } finally {
      setDeletingId(null);
    }
  };

  const cancelCreate = () => {
    setShowCreateForm(false);
    setCreateEmail('');
    setExpiryEnabled(false);
    setExpiryDays('');
    setExpiryTime('12:00');
  };

  // ── Render ──

  return (
    <div className="subUsers">
      {/* Header */}
      <div className="pageHeader">
        <button className="backButton" onClick={() => navigate('/settings')}>
          <img src={arrowLeftIcon} alt={t('back')} />
        </button>
        <h3>{t('subUsers.label')}</h3>
      </div>

      <div className="subUsersContent">
        {/* Top row: count + create button */}
        <div className="subUsersHeader">
          <span className="usersCount">
            {t('subUsers.count', { count: users.length, max: MAX_USERS })}
          </span>
          {users.length < MAX_USERS && !showCreateForm && (
            <button className="createButton" onClick={() => setShowCreateForm(true)}>
              + {t('create')}
            </button>
          )}
        </div>

        {/* Create Form */}
        {showCreateForm && (
          <div className="createForm">
            <h4>{t('subUsers.createNew')}</h4>

            <div className="emailInputRow">
              <label htmlFor="createEmail">{t('email')}</label>
              <input
                id="createEmail"
                type="email"
                value={createEmail}
                onChange={e => setCreateEmail(e.target.value)}
                placeholder={t('subUsers.emailPlaceholder')}
                autoFocus
              />
            </div>

            <div className="formRow">
              <span>{t('subUsers.expiryLabel')}</span>
              <div className="toggleSwitch">
                <input
                  type="checkbox"
                  id="expiryToggle"
                  checked={expiryEnabled}
                  onChange={() => setExpiryEnabled(v => !v)}
                />
                <label htmlFor="expiryToggle" className="toggleLabel">
                  <span className="toggleSlider">
                    <img src={iconCross} alt={t('off')} className="icon icon-cross" />
                    <img src={iconCheck} alt={t('on')} className="icon icon-check" />
                  </span>
                </label>
              </div>
            </div>

            {expiryEnabled && (
              <div className="expiryFields">
                <div className="expiryField">
                  <label htmlFor="expiryDays">{t('subUsers.expiryDays')}</label>
                  <input
                    id="expiryDays"
                    type="number"
                    min="1"
                    max="365"
                    value={expiryDays}
                    onChange={e => setExpiryDays(e.target.value)}
                    placeholder={t('subUsers.expiryDaysPlaceholder')}
                  />
                </div>
                <div className="expiryField">
                  <label htmlFor="expiryTime">{t('subUsers.expiryTime')}</label>
                  <input
                    id="expiryTime"
                    type="time"
                    value={expiryTime}
                    onChange={e => setExpiryTime(e.target.value)}
                  />
                </div>
              </div>
            )}

            <div className="formActions">
              <button className="cancelButton" onClick={cancelCreate} disabled={isCreating}>
                {t('cancel')}
              </button>
              <button
                className="confirmCreateButton"
                onClick={handleCreate}
                disabled={isCreating || !createEmail.trim() || (expiryEnabled && !expiryDays)}
              >
                {isCreating ? t('loading') + '...' : t('create')}
              </button>
            </div>
          </div>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="loadingState">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="spin">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" strokeDasharray="30 20" strokeLinecap="round"/>
            </svg>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && users.length === 0 && !showCreateForm && (
          <div className="emptyState">
            <p>{t('subUsers.emptyState')}</p>
            <p className="emptyHint">{t('subUsers.emptyHint')}</p>
          </div>
        )}

        {/* User list */}
        {!isLoading && (
          <div className="userList">
            {users.map(user => {
              const expired = isExpired(user);
              const status = expired ? 'expired' : user.isActive ? 'active' : 'inactive';
              const isExpanded = expandedUserId === user.userId;
              const isToggling = togglingId === user.userId;
              const isDeleting = deletingId === user.userId;
              const savedPassword = passwordMap.get(user.userId);

              return (
                <div key={user.userId} className={`userCard status-${status} ${isDeleting ? 'deleting' : ''}`}>
                  {/* Main row */}
                  <div className="userCardMain">
                    <div className="userInfo">
                      <span className="username">{user.username}</span>
                      <span className="userEmail">{user.email}</span>
                      <div className="statusBadge">
                        {status === 'active' && <span className="badge active">{t('active')}</span>}
                        {status === 'inactive' && <span className="badge inactive">{t('inactive')}</span>}
                        {status === 'expired' && <span className="badge expired">{t('subUsers.expired')}</span>}
                        {user.expiresAt && status !== 'expired' && (
                          <span className="expiry">
                            {t('subUsers.expiryUntil', { date: formatExpiry(user.expiresAt, i18n.language) })}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="userActions">
                      {/* Gear button */}
                      <button
                        className={`iconButton gearButton ${isExpanded ? 'active' : ''}`}
                        onClick={() => setExpandedUserId(isExpanded ? null : user.userId)}
                        aria-label={t('details')}
                        disabled={isDeleting}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>

                      {/* Active toggle */}
                      <div className={`toggleSwitch smallToggle ${isToggling ? 'loading' : ''}`}>
                        <input
                          type="checkbox"
                          id={`toggle_${user.userId}`}
                          checked={user.isActive && !expired}
                          onChange={() => !expired && !isToggling && handleToggleActive(user.userId)}
                          disabled={expired || isToggling || isDeleting}
                        />
                        <label htmlFor={`toggle_${user.userId}`} className="toggleLabel">
                          <span className="toggleSlider">
                            <img src={iconCross} alt={t('off')} className="icon icon-cross" />
                            <img src={iconCheck} alt={t('on')} className="icon icon-check" />
                          </span>
                        </label>
                      </div>

                      {/* Delete button */}
                      <button
                        className="iconButton deleteButton"
                        onClick={() => handleDelete(user.userId)}
                        aria-label={t('delete')}
                        disabled={isDeleting || isToggling}
                      >
                        {isDeleting ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="spin">
                            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" strokeDasharray="30 20" strokeLinecap="round"/>
                          </svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Expanded gear panel */}
                  {isExpanded && (
                    <div className="userGearPanel">
                      {savedPassword && (
                        <div className="passwordRow">
                          <span className="label">{t('password')}:</span>
                          <span className="passwordValue">{savedPassword}</span>
                          <button
                            className="copyButton"
                            onClick={() => navigator.clipboard.writeText(savedPassword)}
                            title={t('copy')}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                              <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </div>
                      )}
                      <div className="emailRow">
                        <span className="label">{t('email')}:</span>
                        <span className="emailValue">{user.email}</span>
                        <button
                          className="copyButton"
                          onClick={() => navigator.clipboard.writeText(user.email)}
                          title={t('copy')}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/*  Newly created / added credentials modal  */}
      {newlyCreated && (
        <div className="modalOverlay" onClick={() => setNewlyCreated(null)}>
          <div className="credentialsModal" onClick={e => e.stopPropagation()}>
            {newlyCreated.isExisting ? (
              <>
                <h3>{t('subUsers.existingUserTitle')}</h3>
                <p className="emailSentNote">✉ {newlyCreated.email}</p>
                <p className="modalSubtitle">{t('subUsers.existingUserNote')}</p>
              </>
            ) : (
              <>
                <h3>{t('subUsers.credentialsTitle')}</h3>
                <p className="modalSubtitle">{t('subUsers.credentialsSubtitle')}</p>

                <div className="credentialRow">
                  <span className="credLabel">{t('username')}:</span>
                  <span className="credValue">{newlyCreated.username}</span>
                  <button
                    className="copyButton"
                    onClick={() => navigator.clipboard.writeText(newlyCreated.username)}
                    title={t('copy')}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
                <div className="credentialRow">
                  <span className="credLabel">{t('password')}:</span>
                  <span className="credValue">{newlyCreated.password}</span>
                  <button
                    className="copyButton"
                    onClick={() => navigator.clipboard.writeText(newlyCreated.password ?? '')}
                    title={t('copy')}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>

                <p className="emailSentNote">✉ {t('subUsers.emailSentTo', { email: newlyCreated.email })}</p>
              </>
            )}

            <div className="modalActions">
              <button className="closeButton fullWidth" onClick={() => setNewlyCreated(null)}>
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default SubUsers;
